/**
 * CivicDressingModule — the civic wave's RESIDUE + FORECAST layer.
 *
 * The legibility doctrine: no floating text in-world — politics is carried by
 * PHYSICAL OBJECTS (a notice board, banners, bunting, obelisks, treaty
 * stones, occupation colors), by AUDIO (the crier's bell), by toasts, and by
 * the docket window the notice board opens (E). Every political beat leaves
 * something standing an order of magnitude longer than the beat itself:
 *
 *   war declared  → a red war banner over the plaza, for the whole campaign
 *   race open     → campaign bunting through election season (+4 h after)
 *   peace signed  → a war memorial obelisk naming the fallen (E to read)
 *   pact active   → a treaty stone on the road between the towns
 *   conquest      → occupation banners in the occupier's color until integrated
 *   crier calls   → the bell + a toast (the herald walks via the server goal)
 *
 * The notice board is the player's civic surface: the docket (election race +
 * countdown, proposed clauses, open petitions, war clock, treaties, the
 * administration's record) and the FRANCHISE actions (vote / sign / prefer /
 * carry), all gated server-side on residency + standing.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { onInteract } from '../lib/interact_input.js';
import { makeSystem, Phase, Sub } from '../engine-compat.js';
import { settlementSites, type SettlementSite } from '../../../shared/settlements.js';
import { townSlots, slotByKey } from '../../../shared/town_layout.js';
import { EVENT_KINDS } from '../../../shared/living_events.js';
import { sfxAt, inverseDistanceGain } from '../lib/sfx.js';
import { warmThenShow } from '../lib/pipeline_warm.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';
import { mergeStaticMeshes } from '../lib/static_merge.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import { createHologramMaterial } from 'enginex/vfx/ShieldMaterial.js';
import { activePreset } from '../lib/quality_tiers.js';
import { stage3Stats } from '../lib/stage3_fx.js';

/** Server strings can carry LLM-authored prose — escape before any innerHTML. */
const esc = (s: string): string =>
  s.replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string));

const DRESS_RANGE_M = 900;
const DRESS_DROP_M = 1100;
const BOARD_REACH_M = 4.5;
const BUNTING_LINGER_US = 4n * 3600n * 1_000_000n;

interface Dressing {
  sid: bigint;
  group: THREE.Group;
  warBanner: THREE.Group | null;
  bunting: THREE.Group | null;
  occupation: THREE.Group | null;
  board: THREE.Group;
  boardPos: { x: number; z: number };
  obelisks: Map<string, THREE.Group>;
}

// Cached per (color, roughness): every dressed town minted a fresh material
// per post / panel / flag (18 cloth materials from 4 colours for the bunting
// alone) — each one a new pipeline variant + uniform block. The cache is
// module-lifetime and stamped shared so undressing (deferDisposeTree) frees
// the town's geometry but never the common materials.
const matCache = new Map<string, THREE.MeshStandardMaterial>();
function mat(color: number, rough = 0.9): THREE.MeshStandardMaterial {
  const k = `m:${color}:${rough}`;
  let m = matCache.get(k);
  if (!m) { m = new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: 0.05 }); m.userData.shared = true; matCache.set(k, m); }
  return m;
}

function clothMat(color: number): THREE.MeshStandardMaterial {
  const k = `c:${color}`;
  let m = matCache.get(k);
  if (!m) { m = new THREE.MeshStandardMaterial({ color, roughness: 0.8, side: THREE.DoubleSide }); m.userData.shared = true; matCache.set(k, m); }
  return m;
}

export class CivicDressingModule implements IModule {
  readonly name = 'CivicDressing';
  private app: App | null = null;
  private root = new THREE.Group();
  private sites: SettlementSite[] = [];
  private seed = '';
  private dressed = new Map<string, Dressing>();
  private stones = new Map<string, THREE.Group>();
  private subbed = false;
  private lastCrierAt = 0;
  private boardEl: HTMLDivElement | null = null;
  private boardSid: bigint = 0n;
  private acc = 0;
  /** Stage 3 hologram emblems over the notice boards (rotated per frame). */
  private holos: THREE.Mesh[] = [];
  private offInteract: (() => void) | null = null;

  constructor(private readonly env: EnvironmentModule) {}

  init(app: App): void {
    this.app = app;
    this.root.name = 'civic-dressing';
    app.world.scene.add(this.root);
    // onInteract: the bound key AND the pad (the literal 'KeyE' left pad / touch / rebound players without the notice board).
    this.offInteract = onInteract(() => this.onInteractPress());
    app.world.addSystem(makeSystem('CivicDressing', Phase.Animation, 46, (dt) => this.tick(dt), Sub.Update));
    (window as unknown as { __civic?: () => object }).__civic = () => this.probe();
    // SATURATED-TAB LAW: subscribe DURING the load gate (the boot batch), not
    // from the first post-load tick — a busy tab's post-load subscription
    // sends can starve forever, leaving the dressing blind to its rows.
    this.ensureSubs();
    bus.on('atlas:world-adopted', () => this.ensureSubs());
  }

  dispose(): void {
    this.offInteract?.(); this.offInteract = null;
    for (const hs of this.sidSubs.values()) for (const h of hs) { try { h.unsubscribe(); } catch { /* gone */ } }
    this.sidSubs.clear();
    for (const d of this.dressed.values()) deferDisposeTree(d.group);
    for (const s of this.stones.values()) deferDisposeTree(s);
    this.dressed.clear();
    this.stones.clear();
    this.boardEl?.remove();
    this.root.removeFromParent();
  }

  // ── subscriptions ─────────────────────────────────────────────────────────

  private ensureSubs(): void {
    if (this.subbed || !atlas.conn) return;
    this.subbed = true;
    const small = [
      'SELECT * FROM election_race', 'SELECT * FROM war_petition', 'SELECT * FROM war_schedule',
      'SELECT * FROM war_memorial', 'SELECT * FROM occupation', 'SELECT * FROM civ_flip_log',
      'SELECT * FROM settlement_treaty', 'SELECT * FROM recall_petition', 'SELECT * FROM war_state',
      'SELECT * FROM civic_sign WHERE identity = :sender',
      'SELECT * FROM war_readiness', 'SELECT * FROM battle_objective',
    ];
    for (const q of small) {
      try { atlas.conn.subscriptionBuilder().subscribe([q]); } catch { /* pre-publish server */ }
    }
    try {
      atlas.conn.db.npcEvent.onInsert((_ctx, row) => this.onNpcEvent(row as { settlementId: bigint; kind: string; detail: string }));
    } catch { /* table absent */ }
  }

  /** Per-town subscription handles, insertion-ordered (oldest first) — a 3-town LRU. */
  private readonly sidSubs = new Map<string, Array<{ unsubscribe(): void }>>();
  private ensureSidSubs(sid: bigint): void {
    const k = String(sid);
    if (!atlas.conn) return;
    const hit = this.sidSubs.get(k);
    if (hit) { this.sidSubs.delete(k); this.sidSubs.set(k, hit); return; } // refresh recency
    // (bug sweep) the old key list capped itself at 3 but dropped the HANDLES:
    // an evicted town's three queries stayed live for the session and the
    // town re-subscribed on its next approach — unbounded growth on any walk
    // between 4+ towns. Keep the handles; unsubscribe on eviction.
    const handles: Array<{ unsubscribe(): void }> = [];
    for (const q of [
      `SELECT * FROM npc_event WHERE settlement_id = ${sid}`,
      `SELECT * FROM charter_clause WHERE settlement_id = ${sid}`,
      `SELECT * FROM mayor_record WHERE settlement_id = ${sid}`,
    ]) {
      try {
        const h = atlas.conn.subscriptionBuilder().subscribe([q]) as { unsubscribe(): void } | undefined;
        if (h) handles.push(h);
      } catch { /* pre-publish */ }
    }
    this.sidSubs.set(k, handles);
    while (this.sidSubs.size > 3) {
      const oldest = this.sidSubs.keys().next().value as string;
      for (const h of this.sidSubs.get(oldest) ?? []) { try { h.unsubscribe(); } catch { /* gone */ } }
      this.sidSubs.delete(oldest);
    }
  }

  private onNpcEvent(row: { settlementId: bigint; kind: string; detail: string }): void {
    if (row.kind !== 'crier') return;
    const site = this.sites.find((s) => BigInt(s.id + 1) === row.settlementId);
    if (!site) return;
    const d = Math.hypot(site.centerX - atlas.localX, site.centerZ - atlas.localZ);
    if (d > 500) return;
    const now = performance.now();
    if (now - this.lastCrierAt < 20_000) return; // one bell per proclamation window
    this.lastCrierAt = now;
    // The bell rings from the town's square, over the roofs (never muffled by the wall you stand beside), fading with the walk out.
    sfxAt('sfx_civic_bell', { x: site.centerX, y: atlas.localY + 14, z: site.centerZ }, { gain: 0.5 * inverseDistanceGain(d, 150, 700), occlude: false, caption: 'Town bell' });
    bus.emit('atlas:status', { text: `🔔 ${row.detail.slice(0, 130)}` });
  }

  // ── tick ──────────────────────────────────────────────────────────────────

  private tick(dt: number): void {
    // Holograms turn every frame (cheap: a handful of meshes); the board's
    // teardown removes them from the list.
    if (this.holos.length) {
      for (let i = this.holos.length - 1; i >= 0; i--) {
        const h = this.holos[i];
        if (!h.parent) { this.holos.splice(i, 1); continue; }
        h.rotation.y += dt * 0.8;
        h.position.y = 3.05 + Math.sin((performance.now() / 1000) * 1.3 + i) * 0.06;
      }
      stage3Stats.holograms = this.holos.length;
    }
    this.acc += dt;
    if (this.acc < 1) return;
    this.acc = 0;
    const conn = atlas.conn;
    if (!conn || atlas.spaceFlight) return;
    this.ensureSubs();
    if (this.sites.length === 0) {
      const w = [...conn.db.world.iter()].find((x) => x.kind === 'commons');
      if (!w) return;
      this.seed = w.terrainSeed;
      this.sites = settlementSites(this.seed);
    }
    const px = atlas.localX, pz = atlas.localZ;
    // Dress / undress nearby settlements.
    for (const site of this.sites) {
      const d = Math.hypot(site.centerX - px, site.centerZ - pz);
      const key = String(site.id);
      const cur = this.dressed.get(key);
      if (cur && d > DRESS_DROP_M) {
        deferDisposeTree(cur.group);
        this.dressed.delete(key);
        continue;
      }
      if (!cur && d < DRESS_RANGE_M) this.dress(site);
    }
    for (const d of this.dressed.values()) this.refresh(d);
    this.refreshTreatyStones(px, pz);
    if (this.boardEl && this.boardSid !== 0n) this.renderBoard(this.boardSid); // live countdowns
  }

  // ── dressing builders ─────────────────────────────────────────────────────

  private plazaOf(site: SettlementSite): { x: number; z: number } {
    try {
      const s = slotByKey(townSlots(this.seed, site), 'plaza');
      return { x: s.x, z: s.z };
    } catch {
      return { x: site.centerX, z: site.centerZ };
    }
  }

  private dress(site: SettlementSite): void {
    const sid = BigInt(site.id + 1);
    this.ensureSidSubs(sid);
    const plaza = this.plazaOf(site);
    const group = new THREE.Group();
    this.root.add(group);
    if (this.app) void warmThenShow(this.app, group); // R4
    // The notice board — always standing, a few strides off the plaza.
    const bx = plaza.x + 6, bz = plaza.z + 4;
    const by = this.env.groundAt(bx, bz);
    const board = new THREE.Group();
    const posts = mat(0x5a4632);
    for (const s of [-0.8, 0.8]) {
      const p = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.09, 2.4, 6), posts);
      p.position.set(s, 1.2, 0);
      board.add(p);
    }
    const panel = new THREE.Mesh(new THREE.BoxGeometry(2.1, 1.3, 0.08), mat(0x7a6a4e, 0.95));
    panel.position.set(0, 1.7, 0);
    board.add(panel);
    const roof = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.08, 0.5), mat(0x4a3826));
    roof.position.set(0, 2.45, 0);
    roof.rotation.x = 0.15;
    board.add(roof);
    // Papers: small pale quads pinned on the panel.
    const paper = new THREE.MeshStandardMaterial({ color: 0xe8e2d0, roughness: 1 });
    for (let i = 0; i < 5; i++) {
      const sheet = new THREE.Mesh(new THREE.PlaneGeometry(0.32, 0.42), paper);
      sheet.position.set(-0.7 + (i % 3) * 0.65, 1.95 - Math.floor(i / 3) * 0.5, 0.05);
      sheet.rotation.z = (i % 2 === 0 ? 1 : -1) * 0.04;
      board.add(sheet);
    }
    // Stage 3: a civic HOLOGRAM emblem hovers over the roof — the engine's
    // fresnel + scanline shell (time-driven in the shader), slowly turning.
    if (activePreset().vfxShaders) {
      try {
        const { material } = createHologramMaterial({ color: [0.45, 0.85, 1.0], intensity: 0.9, rimPower: 1.8, scanFrequency: 16, scanSpeed: 2.2, scanStrength: 0.4 });
        const holo = new THREE.Mesh(new THREE.IcosahedronGeometry(0.28, 1), material);
        holo.name = 'civic-hologram';
        holo.position.set(0, 3.05, 0);
        holo.renderOrder = 6;
        board.add(holo);
        this.holos.push(holo);
        stage3Stats.holograms = this.holos.length;
      } catch { /* best-effort */ }
    }
    // Nine static pieces on four materials → four draws (the turning hologram stays its own object).
    mergeStaticMeshes(board, (m) => m.name === 'civic-hologram');
    board.position.set(bx, by, bz);
    board.rotation.y = Math.atan2(plaza.x - bx, plaza.z - bz);
    group.add(board);
    this.dressed.set(String(site.id), {
      sid, group, warBanner: null, bunting: null, occupation: null,
      board, boardPos: { x: bx, z: bz }, obelisks: new Map(),
    });
  }

  private buildWarBanner(d: Dressing, plaza: { x: number; z: number }): THREE.Group {
    const g = new THREE.Group();
    const y = this.env.groundAt(plaza.x - 5, plaza.z - 5);
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.18, 14, 8), mat(0x3a3a40, 0.6));
    pole.position.set(0, 7, 0);
    g.add(pole);
    const cloth = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 4.2), new THREE.MeshStandardMaterial({
      color: 0x8a1414, emissive: 0x4a0808, emissiveIntensity: 0.55, roughness: 0.7, side: THREE.DoubleSide,
    }));
    cloth.position.set(1.4, 11.4, 0);
    g.add(cloth);
    g.position.set(plaza.x - 5, y, plaza.z - 5);
    d.group.add(g);
    return g;
  }

  private buildBunting(d: Dressing, plaza: { x: number; z: number }): THREE.Group {
    const g = new THREE.Group();
    const y = this.env.groundAt(plaza.x, plaza.z);
    const post = mat(0x5a4632);
    const colors = [0xc23b3b, 0x3b66c2, 0xd9a94e, 0xe8e2d0];
    for (const [ax, az, bx2, bz2] of [[-7, -2, 7, -4], [-6, 5, 7, 3]] as const) {
      for (const [x, z] of [[ax, az], [bx2, bz2]] as const) {
        const p = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.07, 3.2, 6), post);
        p.position.set(x, 1.6, z);
        g.add(p);
      }
      const n = 9;
      for (let i = 0; i < n; i++) {
        const t = (i + 0.5) / n;
        const x = ax + (bx2 - ax) * t, z = az + (bz2 - az) * t;
        const sag = Math.sin(t * Math.PI) * 0.7;
        const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.4), clothMat(colors[i % colors.length]));
        flag.position.set(x, 3.05 - sag, z);
        flag.rotation.y = Math.atan2(bx2 - ax, bz2 - az) + Math.PI / 2;
        g.add(flag);
      }
    }
    mergeStaticMeshes(g); // 4 posts + 18 pennants on five materials: 22 draws → 5
    g.position.set(plaza.x, y, plaza.z);
    d.group.add(g);
    return g;
  }

  private buildOccupation(d: Dressing, plaza: { x: number; z: number }, civ: string): THREE.Group {
    const g = new THREE.Group();
    const color = civ === 'a' ? 0xd9a94e : 0x7fc4e8;
    for (let i = 0; i < 3; i++) {
      const ang = (i / 3) * Math.PI * 2 + 0.6;
      const x = plaza.x + Math.cos(ang) * 9, z = plaza.z + Math.sin(ang) * 9;
      const y = this.env.groundAt(x, z);
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.12, 6.5, 6), mat(0x3a3a40, 0.6));
      pole.position.set(x, y + 3.25, z);
      const cloth = new THREE.Mesh(new THREE.PlaneGeometry(1.3, 2.2), clothMat(color));
      cloth.position.set(x + 0.7, y + 5.4, z);
      g.add(pole, cloth);
    }
    mergeStaticMeshes(g); // three standards: 6 draws → 2
    d.group.add(g);
    return g;
  }

  private buildObelisk(d: Dressing, mem: { x: number; z: number; inscription: string }): THREE.Group {
    const g = new THREE.Group();
    const y = this.env.groundAt(mem.x, mem.z);
    const base = new THREE.Mesh(new THREE.BoxGeometry(1.6, 0.4, 1.6), mat(0x6a6a72, 0.95));
    base.position.set(0, 0.2, 0);
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.28, 0.5, 3.6, 4), mat(0x8a8a92, 0.85));
    shaft.position.set(0, 2.2, 0);
    shaft.rotation.y = Math.PI / 4;
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.32, 0.5, 4), mat(0x9a9aa2, 0.7));
    tip.position.set(0, 4.25, 0);
    tip.rotation.y = Math.PI / 4;
    g.add(base, shaft, tip);
    g.position.set(mem.x, y, mem.z);
    // Under the SITE's dressing group (untransformed, world-space children like the board): an
    // undress disposes it with the rest. It hung off this.root, so every drop / re-dress near a
    // memorial orphaned one obelisk in the scene and built a duplicate over it (2026-09-22).
    d.group.add(g);
    if (this.app) void warmThenShow(this.app, g); // R4
    return g;
  }

  private refresh(d: Dressing): void {
    const conn = atlas.conn;
    if (!conn) return;
    const site = this.sites.find((s) => BigInt(s.id + 1) === d.sid);
    if (!site) return;
    const plaza = this.plazaOf(site);
    // War banner while any declared war involves the town.
    let atWar = false;
    try {
      for (const w of conn.db.warState.iter()) {
        if (w.status === 'declared' && (w.aggressorSid === d.sid || w.defenderSid === d.sid)) { atWar = true; break; }
      }
    } catch { /* table absent */ }
    if (atWar && !d.warBanner) d.warBanner = this.buildWarBanner(d, plaza);
    if (!atWar && d.warBanner) { deferDisposeTree(d.warBanner); d.warBanner = null; }
    // Campaign bunting while a race is open, lingering 4 h after the tally.
    let festive = false;
    try {
      const nowUs = BigInt(Math.round(Date.now())) * 1000n;
      for (const r of conn.db.electionRace.iter()) {
        if (r.settlementId !== d.sid) continue;
        if (r.state !== 'done') { festive = true; break; }
        if (nowUs - r.updatedAt.microsSinceUnixEpoch < BUNTING_LINGER_US) { festive = true; break; }
      }
    } catch { /* absent */ }
    if (festive && !d.bunting) d.bunting = this.buildBunting(d, plaza);
    if (!festive && d.bunting) { deferDisposeTree(d.bunting); d.bunting = null; }
    // Occupation colors while occupied.
    let occCiv = '';
    try {
      const o = conn.db.occupation.settlementId.find(d.sid);
      if (o && o.state === 'occupied') occCiv = o.occupierCiv;
    } catch { /* absent */ }
    if (occCiv && !d.occupation) d.occupation = this.buildOccupation(d, plaza, occCiv);
    if (!occCiv && d.occupation) { deferDisposeTree(d.occupation); d.occupation = null; }
    // Memorial obelisks (persist as long as the rows do — and go when a row does).
    try {
      const live = new Set<string>();
      for (const m of conn.db.warMemorial.iter()) {
        if (m.settlementId !== d.sid) continue;
        const k = String(m.memId);
        live.add(k);
        if (!d.obelisks.has(k)) d.obelisks.set(k, this.buildObelisk(d, m));
      }
      for (const [k, g] of d.obelisks) if (!live.has(k)) { deferDisposeTree(g); d.obelisks.delete(k); }
    } catch { /* absent */ }
  }

  private refreshTreatyStones(px: number, pz: number): void {
    const conn = atlas.conn;
    if (!conn) return;
    const live = new Set<string>();
    try {
      for (const tr of conn.db.settlementTreaty.iter()) {
        if (tr.status !== 'active') continue;
        const a = this.sites.find((s) => BigInt(s.id + 1) === tr.aId);
        const b = this.sites.find((s) => BigInt(s.id + 1) === tr.bId);
        if (!a || !b) continue;
        const mx = (a.centerX + b.centerX) / 2, mz = (a.centerZ + b.centerZ) / 2;
        if (Math.hypot(mx - px, mz - pz) > DRESS_RANGE_M) continue;
        const k = String(tr.treatyId);
        live.add(k);
        if (this.stones.has(k)) continue;
        const g = new THREE.Group();
        const y = this.env.groundAt(mx, mz);
        const stoneM = mat(0x7a7a82, 0.95);
        for (const s of [-1, 1]) {
          const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.5, 2.6, 0.5), stoneM);
          pillar.position.set(s * 1.1, 1.3, 0);
          g.add(pillar);
        }
        const lintel = new THREE.Mesh(new THREE.BoxGeometry(3.0, 0.45, 0.6), stoneM);
        lintel.position.set(0, 2.8, 0);
        g.add(lintel);
        if (tr.kind === 'alliance') {
          const cloth = new THREE.Mesh(new THREE.PlaneGeometry(0.9, 1.4), clothMat(0xe8e2d0));
          cloth.position.set(0, 2.0, 0);
          g.add(cloth);
        }
        mergeStaticMeshes(g); // two pillars + the lintel are one stone
        g.position.set(mx, y, mz);
        g.rotation.y = Math.atan2(b.centerX - a.centerX, b.centerZ - a.centerZ) + Math.PI / 2;
        this.root.add(g);
        if (this.app) void warmThenShow(this.app, g); // R4
        this.stones.set(k, g);
      }
    } catch { /* absent */ }
    for (const [k, g] of [...this.stones]) {
      if (!live.has(k)) { deferDisposeTree(g); this.stones.delete(k); }
    }
  }

  // ── the docket window + franchise actions ─────────────────────────────────

  private onInteractPress(): void {
    if (this.boardEl) { this.closeBoard(); return; }
    const px = atlas.localX, pz = atlas.localZ;
    // Nearest interactable: notice board first, then a live staged event, then an obelisk.
    for (const d of this.dressed.values()) {
      if (Math.hypot(d.boardPos.x - px, d.boardPos.z - pz) <= BOARD_REACH_M) { this.openBoard(d.sid); return; }
      for (const [k, g] of d.obelisks) {
        if (Math.hypot(g.position.x - px, g.position.z - pz) <= BOARD_REACH_M) { this.openMemorial(d.sid, k); return; }
      }
    }
    const conn = atlas.conn;
    if (!conn) return;
    try {
      for (const ev of conn.db.livingEvent.iter()) {
        if (ev.phase !== 'live') continue;
        const def = EVENT_KINDS[ev.kind];
        const reach = (def?.crowdRadiusM ?? 8) + 20;
        if (Math.hypot(ev.x - px, ev.z - pz) > reach) continue;
        conn.reducers.witnessCivicEvent({ eventId: ev.eventId });
        bus.emit('atlas:status', { text: `You stand with the town at the ${def?.label ?? ev.kind}.` });
        sfxAt('sfx_crowd_murmur', { x: ev.x, y: atlas.localY + 1, z: ev.z }, { gain: 0.35, cooldownMs: 4000, caption: 'Crowd murmurs' });
        return;
      }
    } catch { /* absent */ }
  }

  private windowShell(title: string): HTMLDivElement {
    const el = document.createElement('div');
    el.id = 'civic-board';
    el.style.cssText = 'position:fixed;top:8%;left:50%;transform:translateX(-50%);width:min(640px,92vw);max-height:80vh;'
      + 'overflow-y:auto;background:color-mix(in srgb, var(--ax-bg2) 93.3%, transparent);color:var(--ax-text);border:1px solid var(--ax-line);border-radius:var(--ax-radius);'
      + 'padding:16px 20px;z-index:2600;font:13px/1.5 var(--ax-font);';
    el.innerHTML = `<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
      <b style="font-size:15px;letter-spacing:.04em">${title}</b>
      <button id="civic-close" style="background:none;border:1px solid var(--ax-line);color:var(--ax-soft);border-radius:var(--ax-radius);cursor:pointer;padding:2px 10px">✕</button></div>
      <div id="civic-body"></div>`;
    document.body.appendChild(el);
    el.querySelector('#civic-close')?.addEventListener('click', () => this.closeBoard());
    return el;
  }

  private openBoard(sid: bigint): void {
    this.boardSid = sid;
    this.boardEl = this.windowShell('⚖ THE TOWN NOTICE BOARD');
    this.renderBoard(sid);
  }

  private openMemorial(sid: bigint, memKey: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    let mem: { inscription: string; fallenJson: string } | null = null;
    try {
      for (const m of conn.db.warMemorial.iter()) { if (String(m.memId) === memKey) { mem = m; break; } }
    } catch { /* absent */ }
    if (!mem) return;
    this.boardSid = 0n;
    this.boardEl = this.windowShell('🕯 THE WAR MEMORIAL');
    const body = this.boardEl.querySelector('#civic-body') as HTMLDivElement;
    let fallen: string[] = [];
    try { fallen = JSON.parse(mem.fallenJson) as string[]; } catch { fallen = []; }
    body.innerHTML = `<p style="font-style:italic;color:var(--ax-text)">${esc(mem.inscription)}</p>`
      + (fallen.length > 0
        ? `<p style="margin-top:10px;color:var(--ax-soft)">THE FALLEN</p><p>${fallen.map((f) => `· ${esc(f)}`).join('<br>')}</p>`
        : '<p style="color:var(--ax-soft)">The stone lists no names — the town buried its grief unmarked.</p>');
  }

  private closeBoard(): void {
    this.boardEl?.remove();
    this.boardEl = null;
    this.boardSid = 0n;
  }

  private act(label: string, fn: () => Promise<unknown> | unknown): string {
    const id = `civ-act-${Math.abs(label.split('').reduce((h, c) => (h * 31 + c.charCodeAt(0)) | 0, 7))}-${label.length}`;
    queueMicrotask(() => {
      this.boardEl?.querySelector(`#${id}`)?.addEventListener('click', async () => {
        try {
          await fn();
          bus.emit('atlas:status', { text: `✓ ${label}` });
        } catch (err) {
          bus.emit('atlas:status', { text: `${(err as Error)?.message ?? 'The clerk refuses.'}` });
        }
      });
    });
    return `<button id="${id}" style="background:var(--ax-bg3);border:1px solid var(--ax-line);color:var(--ax-text);border-radius:var(--ax-radius);cursor:pointer;padding:2px 10px;margin:2px 4px 2px 0">${label}</button>`;
  }

  private renderBoard(sid: bigint): void {
    const conn = atlas.conn;
    const body = this.boardEl?.querySelector('#civic-body') as HTMLDivElement | null;
    if (!conn || !body) return;
    const H: string[] = [];
    const sec = (t: string): void => { H.push(`<p style="margin:12px 0 4px;color:var(--ax-hi);letter-spacing:.08em;font-size:11px">${t}</p>`); };
    const nowUs = BigInt(Math.round(Date.now())) * 1000n;
    const inMin = (ts: { microsSinceUnixEpoch: bigint }): string => {
      const s = Number((ts.microsSinceUnixEpoch - nowUs) / 1_000_000n);
      if (s <= 0) return 'any moment';
      return s < 3600 ? `${Math.ceil(s / 60)} min` : `${(s / 3600).toFixed(1)} h`;
    };
    try {
      // The election — race, countdown, franchise.
      sec('THE MAYOR’S SEAT');
      const econ = conn.db.settlementEcon.settlementId.find(sid);
      const race = [...conn.db.electionRace.iter()].find((r) => r.settlementId === sid && r.state !== 'done');
      if (race) {
        H.push(`<p>THE RACE IS ON — the ${esc(race.aFaction || 'standing')} candidate against ${race.bFaction ? `the ${esc(race.bFaction)} challenger` : 'no challenger'}; `
          + `the tally is read in <b>${inMin(race.tallyAt)}</b> (${esc(race.state)}).</p>`);
        H.push('<p>');
        for (const f of ['labor', 'commerce', 'civic']) {
          H.push(this.act(`Prefer ${f}`, () => conn.reducers.castMayorPreference({ settlementId: sid, faction: f })));
        }
        H.push(this.act('Carry a message (labor)', () => conn.reducers.carryCampaignMessage({ settlementId: sid, faction: 'labor' })));
        H.push(this.act('Carry a message (commerce)', () => conn.reducers.carryCampaignMessage({ settlementId: sid, faction: 'commerce' })));
        H.push('</p>');
      } else if (econ) {
        H.push(`<p>The next election opens in <b>${inMin(econ.nextElectionAt)}</b>. Campaign season runs the final 90 minutes.</p>`);
      }
      const rec = [...conn.db.mayorRecord.iter()].filter((r) => r.settlementId === sid).sort((a, b) => (a.recId < b.recId ? 1 : -1))[0];
      if (rec) {
        H.push(`<p style="color:var(--ax-soft)">The administration’s record: ${rec.lawsPassed} law${rec.lawsPassed === 1 ? '' : 's'} passed`
          + `${rec.warsWon + rec.warsLost > 0 ? `, wars ${rec.warsWon}–${rec.warsLost}` : ''}.</p>`);
      }
      // Proposed clauses — the vote.
      const proposed = [...conn.db.charterClause.iter()].filter((c) => c.settlementId === sid && c.status === 'proposed');
      if (proposed.length > 0) {
        sec('BEFORE THE TOWN — PROPOSED LAW');
        for (const c of proposed) {
          H.push(`<p><b>${esc(c.kind.toUpperCase())} ${c.value}</b> — “${esc(c.rationale.slice(0, 90))}” · tally in ${inMin(c.voteAt)} · ${c.yesVotes}–${c.noVotes} `
            + this.act('Aye', () => conn.reducers.castCivicVote({ settlementId: sid, clauseId: c.clauseId, support: true }))
            + this.act('Nay', () => conn.reducers.castCivicVote({ settlementId: sid, clauseId: c.clauseId, support: false }))
            + '</p>');
        }
      }
      // Petitions.
      const recalls = [...conn.db.recallPetition.iter()].filter((p) => p.settlementId === sid && p.status === 'open');
      const vetoes = [...conn.db.warPetition.iter()].filter((p) => p.settlementId === sid && p.state === 'open');
      if (recalls.length + vetoes.length > 0) {
        sec('NAILED TO THE DOOR — PETITIONS');
        for (const p of recalls) {
          H.push(`<p>RECALL the mayor — ${p.signatures} signature${p.signatures === 1 ? '' : 's'} `
            + this.act('Sign the recall', () => conn.reducers.signRecallPetition({ petitionId: p.petitionId })) + '</p>');
        }
        for (const p of vetoes) {
          H.push(`<p>THE PEACE ROLL — ${p.signatures} of ${p.threshold} names against the march `
            + this.act('Sign for peace', () => conn.reducers.signWarPetition({ warId: p.warId })) + '</p>');
        }
      }
      // War & peace.
      const wars = [...conn.db.warState.iter()].filter((w) => (w.aggressorSid === sid || w.defenderSid === sid) && (w.status === 'declared' || w.status === 'council'));
      if (wars.length > 0) {
        sec('THE WAR');
        for (const w of wars) {
          if (w.status === 'council') { H.push('<p>The WAR COUNCIL sits — the town awaits the final word.</p>'); continue; }
          const sched = conn.db.warSchedule.warId.find(w.warId);
          H.push(`<p>WAR — exhaustion ${w.exhaustionA}/${w.exhaustionB}, ${w.casualties} fallen`
            + (sched ? `; the next battle opens in <b>${inMin(sched.nextBattleAt)}</b>` : '') + '.</p>');
          // War rework 2026-09-16: the readiness blocker and the open battle's objective.
          const rdy = conn.db.warReadiness.warId.find(w.warId);
          const blocker = rdy ? (w.aggressorSid === sid ? rdy.blockerA : rdy.blockerB) : '';
          if (blocker) H.push(`<p>THE MUSTER — ${esc(blocker)}.</p>`);
          const open = [...conn.db.battle.iter()].find((b) => b.warId === w.warId && b.phase !== 'resolved');
          if (open) {
            const obj = [...conn.db.battleObjective.iter()].find((o) => o.battleId === open.battleId);
            if (obj) H.push(`<p>THE FIELD — the ${obj.kind === 'gate' ? 'gate' : 'crossing'} is ${esc(obj.state)}${obj.controller ? ` by the ${obj.controller === 'a' ? 'attackers' : 'defenders'}` : ''} (${Math.round(obj.progress * 100)} %).</p>`);
          }
        }
      }
      const occ = conn.db.occupation.settlementId.find(sid);
      if (occ && occ.state === 'occupied') {
        sec('THE OCCUPATION');
        H.push(`<p>The ${occ.occupierCiv === 'a' ? 'Aurelian' : 'Borean'} banner holds the square — compliance ${occ.compliance}, resistance ${occ.resistance}.</p>`);
      }
      // Treaties.
      const pacts = [...conn.db.settlementTreaty.iter()].filter((t) => (t.aId === sid || t.bId === sid) && t.status === 'active');
      if (pacts.length > 0) {
        sec('STANDING ACCORDS');
        for (const t of pacts) H.push(`<p>${t.kind === 'nonaggression' ? 'Non-aggression pact' : t.kind === 'trade' ? 'Trade pact (duty waived)' : 'ALLIANCE'} with settlement ${t.aId === sid ? t.bId : t.aId}.</p>`);
      }
      H.push('<p style="margin-top:12px;color:var(--ax-muted);font-size:11px">The franchise is earned: a lease at the town and its regard. '
        + 'Votes and signatures are counted at the tally, one voice among the town’s.</p>');
    } catch (err) {
      H.push(`<p>The board is bare (${(err as Error)?.message ?? 'no rows yet'}).</p>`);
    }
    body.innerHTML = H.join('');
  }

  private probe(): object {
    return {
      dressed: this.dressed.size,
      warBanners: [...this.dressed.values()].filter((d) => d.warBanner).length,
      bunting: [...this.dressed.values()].filter((d) => d.bunting).length,
      occupations: [...this.dressed.values()].filter((d) => d.occupation).length,
      obelisks: [...this.dressed.values()].reduce((n, d) => n + d.obelisks.size, 0),
      treatyStones: this.stones.size,
      boardOpen: !!this.boardEl,
    };
  }
}
